/**
 * AdminAuthContext — Supabase-based admin authentication
 * ─────────────────────────────────────────────────────────────
 *
 * Auth flow:
 *   1. On mount: check localStorage for persisted admin session.
 *   2. If session found → restore currentAdmin, isAuthenticated = true.
 *   3. login(email, password):
 *      a. Verify password against VITE_ADMIN_PASSWORD env var.
 *      b. Query Supabase admin_users WHERE email = input AND is_active = true.
 *      c. If found → persist to localStorage, set isAuthenticated = true.
 *   4. logout: clear localStorage, reset state → AdminGate redirects to /admin/login.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getSupabaseClient, isSupabaseConfigured } from "../lib/supabaseClient";
import type { AdminUser } from "../store/types";

const SESSION_KEY = "tabifoto_admin_session";

// ── persistence helpers ───────────────────────────────────────────────────────

function readSession(): AdminUser | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdminUser;
  } catch {
    return null;
  }
}

function writeSession(user: AdminUser): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  } catch { /* storage blocked */ }
}

function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch { /* storage blocked */ }
}

// ── context types ─────────────────────────────────────────────────────────────

interface AdminAuthContextValue {
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  currentAdmin: AdminUser | null;
  /**
   * Login with email + password.
   * Validates password against env var, then queries admin_users for active account.
   * Throws on invalid credentials or inactive account.
   */
  login: (email: string, password: string) => Promise<void>;
  /** Clears session and resets auth state. */
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

// ── provider ─────────────────────────────────────────────────────────────────

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [currentAdmin, setCurrentAdmin] = useState<AdminUser | null>(null);
  const bootedRef = useRef(false);

  // bootstrap from localStorage
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    const stored = readSession();
    if (stored) {
      setCurrentAdmin(stored);
      setIsAuthenticated(true);
    }
    setIsBootstrapping(false);
  }, []);

  // login
  const login = useCallback(async (email: string, password: string) => {
    const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD as string | undefined;

    // password check
    if (!adminPassword || password !== adminPassword) {
      throw new Error("INVALID_CREDENTIALS");
    }

    // Supabase lookup
    if (!isSupabaseConfigured()) {
      throw new Error("SUPABASE_NOT_CONFIGURED");
    }

    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from("admin_users")
      .select("id, email, role, is_active")
      .eq("email", email.toLowerCase().trim())
      .eq("is_active", true)
      .maybeSingle();

    if (error) {
      throw new Error("DB_ERROR");
    }

    if (!data) {
      throw new Error("INVALID_CREDENTIALS");
    }

    const user: AdminUser = {
      id: String(data.id),
      email: String(data.email),
      role: String(data.role),
    };

    writeSession(user);
    setCurrentAdmin(user);
    setIsAuthenticated(true);
  }, []);

  // logout
  const logout = useCallback(() => {
    clearSession();
    setCurrentAdmin(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{ isAuthenticated, isBootstrapping, currentAdmin, login, logout }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
